import React from 'react';
import { SocialPlatform } from '../../../../common/models/social-scheduler.model';

/* ─── Authentic platform SVG logos ─────────────────────────────
 * Each logo is rendered inline so we control colors and can
 * scale perfectly at any size. Brand marks follow official
 * guidelines (instagram gradient, facebook blue, tiktok tri-color,
 * linkedin blue).
 * ──────────────────────────────────────────────────────────── */

const InstagramLogo: React.FC<{ size: number }> = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
        <defs>
            <radialGradient id="ig-grad" cx="28%" cy="107%" r="130%">
                <stop offset="0%" stopColor="#FFDD55" />
                <stop offset="10%" stopColor="#FFDD55" />
                <stop offset="50%" stopColor="#FF543E" />
                <stop offset="100%" stopColor="#C837AB" />
            </radialGradient>
        </defs>
        <rect x="0" y="0" width="40" height="40" rx="10" fill="url(#ig-grad)" />
        <rect x="9" y="9" width="22" height="22" rx="6" fill="none" stroke="#FFFFFF" strokeWidth="2.2" />
        <circle cx="20" cy="20" r="5" fill="none" stroke="#FFFFFF" strokeWidth="2.2" />
        <circle cx="27.5" cy="12.5" r="1.4" fill="#FFFFFF" />
    </svg>
);

const FacebookLogo: React.FC<{ size: number }> = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
        <circle cx="20" cy="20" r="20" fill="#1877F2" />
        <path
            d="M23.2 21.3h3l.5-3.8h-3.5v-2.4c0-1.1.3-1.9 2-1.9h2.1V9.8c-.4-.1-1.6-.2-3-.2-3 0-5 1.8-5 5.2v2.9h-3.4v3.8h3.4v9.8h4v-9.8z"
            fill="#FFFFFF"
        />
    </svg>
);

const TikTokLogo: React.FC<{ size: number }> = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
        <rect x="0" y="0" width="40" height="40" rx="9" fill="#000000" />
        {/* Cyan offset */}
        <path
            d="M26.5 10.5c0 2.6 2.1 4.7 4.7 4.7v3.5c-1.7 0-3.3-.5-4.7-1.3v7.9c0 3.8-3 6.9-6.8 6.9-3.7 0-6.8-3.1-6.8-6.9 0-3.6 2.9-6.6 6.4-6.8v3.5c-1.6.2-2.9 1.6-2.9 3.3 0 1.8 1.4 3.3 3.3 3.3 1.8 0 3.3-1.4 3.3-3.3V10.5h3.5z"
            fill="#25F4EE"
            opacity="0.85"
            transform="translate(-1 0.5)"
        />
        {/* Red offset */}
        <path
            d="M26.5 10.5c0 2.6 2.1 4.7 4.7 4.7v3.5c-1.7 0-3.3-.5-4.7-1.3v7.9c0 3.8-3 6.9-6.8 6.9-3.7 0-6.8-3.1-6.8-6.9 0-3.6 2.9-6.6 6.4-6.8v3.5c-1.6.2-2.9 1.6-2.9 3.3 0 1.8 1.4 3.3 3.3 3.3 1.8 0 3.3-1.4 3.3-3.3V10.5h3.5z"
            fill="#FE2C55"
            opacity="0.85"
            transform="translate(1 -0.5)"
        />
        {/* White top */}
        <path
            d="M26.5 10.5c0 2.6 2.1 4.7 4.7 4.7v3.5c-1.7 0-3.3-.5-4.7-1.3v7.9c0 3.8-3 6.9-6.8 6.9-3.7 0-6.8-3.1-6.8-6.9 0-3.6 2.9-6.6 6.4-6.8v3.5c-1.6.2-2.9 1.6-2.9 3.3 0 1.8 1.4 3.3 3.3 3.3 1.8 0 3.3-1.4 3.3-3.3V10.5h3.5z"
            fill="#FFFFFF"
        />
    </svg>
);

const LinkedInLogo: React.FC<{ size: number }> = ({ size }) => (
    <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden>
        <rect x="0" y="0" width="40" height="40" rx="6" fill="#0A66C2" />
        <path
            d="M13.6 30.5h-4.2V17h4.2v13.5zM11.5 15.2c-1.3 0-2.4-1.1-2.4-2.4s1.1-2.4 2.4-2.4 2.4 1.1 2.4 2.4-1.1 2.4-2.4 2.4zM30.9 30.5h-4.2v-6.6c0-1.6 0-3.6-2.2-3.6-2.2 0-2.5 1.7-2.5 3.5v6.7H17.8V17h4v1.8h.1c.6-1.1 1.9-2.2 3.9-2.2 4.2 0 5 2.8 5 6.4v7.5z"
            fill="#FFFFFF"
        />
    </svg>
);

const logoMap: Record<SocialPlatform, React.FC<{ size: number }>> = {
    instagram: InstagramLogo,
    meta: FacebookLogo,
    tiktok: TikTokLogo,
    linkedin: LinkedInLogo,
};

// Kept for backwards compatibility — used by PostCalendar for dots, PostCard for status colors
const platformConfig: Record<SocialPlatform, { color: string; label: string }> = {
    instagram: { color: '#E1306C', label: 'Instagram' },
    meta: { color: '#1877F2', label: 'Meta' },
    tiktok: { color: '#000000', label: 'TikTok' },
    linkedin: { color: '#0A66C2', label: 'LinkedIn' },
};

interface PlatformIconProps {
    platform: SocialPlatform;
    size?: number;
}

export const PlatformIcon: React.FC<PlatformIconProps> = ({ platform, size = 24 }) => {
    const Logo = logoMap[platform] || logoMap.meta;

    return (
        <span
            className="inline-flex shrink-0 items-center justify-center"
            style={{ width: size, height: size }}
            aria-label={platformConfig[platform]?.label || platform}
            role="img"
        >
            <Logo size={size} />
        </span>
    );
};

export { platformConfig };
